import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchFixations } from '@/entities/fixation';
import { assignFixationAction, releaseFixationAction } from '@/features/manage-fixation';
import { useAgentScope } from '@/features/agent-scope';
import { ApiError } from '@/shared/api';
import { fixationStatusLabel, uiCopy } from '@/shared/copy';
import { Alert, AlertDescription, AlertTitle, Badge, Button, DirectoryPanel, SearchField, Sheet, Skeleton, StageTabs, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '@/shared/ui';
import { BrandManagerPicker } from '@/widgets/brand-manager-picker';
import { FreeAgentPicker } from '@/widgets/free-agent-picker';
const STATUS_TABS = [
    { id: 'all', label: 'Все' },
    { id: 'ACTIVE', label: 'Активна' },
    { id: 'ENDING', label: 'Заканчивается' },
    { id: 'NONE', label: 'Нет фиксации' },
];
function parseStatus(value) {
    if (value === 'ACTIVE' || value === 'ENDING' || value === 'NONE')
        return value;
    return 'all';
}
function normalize(value) {
    return value.trim().toLocaleLowerCase('ru-RU').replaceAll('ё', 'е');
}
function pluralFixations(count) {
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 === 1 && mod100 !== 11)
        return 'фиксация';
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
        return 'фиксации';
    }
    return 'фиксаций';
}
function fixationTone(status) {
    if (status === 'ACTIVE')
        return 'success';
    if (status === 'ENDING')
        return 'warning';
    return 'neutral';
}
function formatDate(iso) {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime()))
        return iso;
    return date.toLocaleDateString('ru-RU');
}
function matchesQuery(item, query) {
    if (!query)
        return true;
    const hay = normalize([item.agentDisplayName, item.assignedManagerName ?? '', fixationStatusLabel(item.fixationStatus)].join(' '));
    return hay.includes(normalize(query));
}
export function FixationList() {
    const { scope } = useAgentScope();
    const [searchParams, setSearchParams] = useSearchParams();
    const status = parseStatus(searchParams.get('status'));
    const query = searchParams.get('q') ?? '';
    const [draftQ, setDraftQ] = useState(query);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);
    const [assignOpen, setAssignOpen] = useState(false);
    const [pickedAgentId, setPickedAgentId] = useState(null);
    const [busy, setBusy] = useState(false);
    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchFixations(scope);
            setItems(data.items);
        }
        catch (err) {
            setError(err instanceof ApiError ? err.message : uiCopy.loadFailed);
        }
        finally {
            setLoading(false);
        }
    }, [scope]);
    useEffect(() => {
        void load();
    }, [load]);
    useEffect(() => {
        setDraftQ(query);
    }, [query]);
    const writeQuery = useCallback((next) => {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            if (!next)
                params.delete('q');
            else
                params.set('q', next);
            return params;
        }, { replace: true });
    }, [setSearchParams]);
    useEffect(() => {
        if (draftQ.trim() === query)
            return;
        const timer = window.setTimeout(() => writeQuery(draftQ.trim()), 250);
        return () => window.clearTimeout(timer);
    }, [draftQ, query, writeQuery]);
    function setStatus(next) {
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            if (!next || next === 'all')
                params.delete('status');
            else
                params.set('status', next);
            return params;
        }, { replace: true });
    }
    function resetFilters() {
        setDraftQ('');
        setSearchParams((prev) => {
            const params = new URLSearchParams(prev);
            params.delete('q');
            params.delete('status');
            return params;
        }, { replace: true });
    }
    function closeAssign() {
        setAssignOpen(false);
        setPickedAgentId(null);
    }
    const searched = useMemo(() => items.filter((item) => matchesQuery(item, query)), [items, query]);
    const rows = useMemo(() => status === 'all'
        ? searched
        : searched.filter((item) => item.fixationStatus === status), [searched, status]);
    const stageTabItems = useMemo(() => STATUS_TABS.map((tab) => ({
        id: tab.id,
        label: tab.label,
        count: tab.id === 'all'
            ? searched.length
            : searched.filter((item) => item.fixationStatus === tab.id).length,
    })), [searched]);
    const filtersActive = query.length > 0 || status !== 'all';
    const showSkeleton = loading && items.length === 0;
    return (_jsxs("div", { children: [_jsxs("div", { className: "space-y-3", children: [_jsx(StageTabs, { items: stageTabItems, value: status, onValueChange: setStatus, "aria-label": "\u0421\u0442\u0430\u0442\u0443\u0441 \u0444\u0438\u043A\u0441\u0430\u0446\u0438\u0438" }), error ? (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertTitle, { children: uiCopy.errorTitle }), _jsx(AlertDescription, { children: error }), _jsx(Button, { type: "button", variant: "outline", className: "mt-3", onClick: () => void load(), children: uiCopy.retry })] })) : null, _jsx(DirectoryPanel, { total: _jsxs(_Fragment, { children: [_jsx("strong", { children: rows.length }), " ", pluralFixations(rows.length)] }), actions: _jsxs(_Fragment, { children: [_jsxs(Button, { type: "button", onClick: () => {
                                        setSelected(null);
                                        setAssignOpen(true);
                                        setPickedAgentId(null);
                                    }, children: [uiCopy.assignAction, " \u0430\u0433\u0435\u043D\u0442\u0430"] }), filtersActive ? (_jsx("button", { type: "button", className: "text-[11px] text-primary hover:underline", onClick: resetFilters, children: "\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C" })) : null] }), search: _jsx(SearchField, { className: "w-full", value: draftQ, onValueChange: setDraftQ, placeholder: "\u0410\u0433\u0435\u043D\u0442 \u0438\u043B\u0438 \u0431\u0440\u0435\u043D\u0434-\u043C\u0435\u043D\u0435\u0434\u0436\u0435\u0440", "aria-label": "\u041F\u043E\u0438\u0441\u043A \u0444\u0438\u043A\u0441\u0430\u0446\u0438\u0439" }), footer: "\u0421\u0442\u0440\u043E\u043A\u0430 \u043E\u0442\u043A\u0440\u044B\u0432\u0430\u0435\u0442 \u0441\u0440\u043E\u043A \u0438 \u0441\u043D\u044F\u0442\u0438\u0435 \u0444\u0438\u043A\u0441\u0430\u0446\u0438\u0438.", children: showSkeleton ? (_jsx(Skeleton, { className: "h-64 w-full rounded-none" })) : error && items.length === 0 ? null : !error && items.length === 0 ? (_jsx("div", { className: "px-4 py-6", children: _jsxs(Alert, { children: [_jsx(AlertTitle, { children: "\u0410\u043A\u0442\u0438\u0432\u043D\u044B\u0445 \u0444\u0438\u043A\u0441\u0430\u0446\u0438\u0439 \u043D\u0435\u0442" }), _jsx(AlertDescription, { children: "\u041A\u043E\u0433\u0434\u0430 \u0430\u0433\u0435\u043D\u0442 \u0431\u0443\u0434\u0435\u0442 \u0437\u0430\u043A\u0440\u0435\u043F\u043B\u0451\u043D, \u043E\u043D \u043F\u043E\u044F\u0432\u0438\u0442\u0441\u044F \u0432 \u044D\u0442\u043E\u043C \u0441\u043F\u0438\u0441\u043A\u0435." })] }) })) : rows.length === 0 ? (_jsx("p", { className: "px-4 py-6 text-sm text-muted-foreground", children: "\u0421\u0442\u0440\u043E\u043A \u043D\u0435 \u043D\u0430\u0439\u0434\u0435\u043D\u043E." })) : (_jsxs(Table, { density: "compact", framed: false, "aria-label": "\u0424\u0438\u043A\u0441\u0430\u0446\u0438\u0438", children: [_jsx(TableHeader, { children: _jsxs(TableRow, { children: [_jsx(TableHead, { children: "\u0410\u0433\u0435\u043D\u0442" }), _jsx(TableHead, { children: "\u0411\u0440\u0435\u043D\u0434-\u043C\u0435\u043D\u0435\u0434\u0436\u0435\u0440" }), _jsx(TableHead, { children: "\u0414\u043E" }), _jsx(TableHead, { children: "\u0421\u0442\u0430\u0442\u0443\u0441" })] }) }), _jsx(TableBody, { children: rows.map((item) => (_jsxs(TableRow, { "data-clickable": "true", tabIndex: 0, onClick: () => setSelected(item), onKeyDown: (event) => {
                                            if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                setSelected(item);
                                            }
                                        }, children: [_jsx(TableCell, { children: _jsx("span", { className: "font-medium text-primary", children: item.agentDisplayName }) }), _jsx(TableCell, { children: item.assignedManagerName || '—' }), _jsx(TableCell, { className: "whitespace-nowrap", children: formatDate(item.fixationEndsAt) }), _jsx(TableCell, { children: _jsx(Badge, { tone: fixationTone(item.fixationStatus), children: fixationStatusLabel(item.fixationStatus) }) })] }, item.assignmentId))) })] })) })] }), _jsx(Sheet, { open: selected !== null, onClose: () => setSelected(null), title: selected?.agentDisplayName ?? 'Фиксация', children: selected ? (_jsxs("div", { className: "space-y-3 text-sm", children: [_jsxs("p", { children: ["\u0414\u043E: ", formatDate(selected.fixationEndsAt)] }), _jsxs("p", { children: ["\u0421\u0442\u0430\u0442\u0443\u0441:", ' ', _jsx(Badge, { tone: fixationTone(selected.fixationStatus), children: fixationStatusLabel(selected.fixationStatus) })] }), selected.assignedManagerName ? (_jsxs("p", { children: ["\u0411\u0440\u0435\u043D\u0434-\u043C\u0435\u043D\u0435\u0434\u0436\u0435\u0440: ", selected.assignedManagerName] })) : null, _jsx(Button, { type: "button", variant: "destructive", disabled: busy, onClick: () => {
                                void (async () => {
                                    setBusy(true);
                                    try {
                                        await releaseFixationAction(selected.assignmentId);
                                        setSelected(null);
                                        await load();
                                    }
                                    catch (err) {
                                        setError(err instanceof ApiError
                                            ? err.message
                                            : 'Не получилось снять фиксацию.');
                                    }
                                    finally {
                                        setBusy(false);
                                    }
                                })();
                            }, children: "\u0421\u043D\u044F\u0442\u044C \u0444\u0438\u043A\u0441\u0430\u0446\u0438\u044E" })] })) : null }), _jsx(Sheet, { open: assignOpen, onClose: closeAssign, title: uiCopy.assignAction, children: pickedAgentId ? (_jsx(BrandManagerPicker, { busy: busy, onPick: (manager) => {
                        void (async () => {
                            setBusy(true);
                            try {
                                await assignFixationAction(pickedAgentId, manager.managerId);
                                closeAssign();
                                await load();
                            }
                            catch (err) {
                                setError(err instanceof ApiError ? err.message : uiCopy.actionFailed);
                            }
                            finally {
                                setBusy(false);
                            }
                        })();
                    } })) : (_jsx(FreeAgentPicker, { busy: busy, onPick: (agent) => setPickedAgentId(agent.agentId) })) })] }));
}
